import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Bell,
  Search,
  Plus,
  CheckCircle2,
  Circle,
  TrendingUp,
  Users,
  FolderKanban,
  Activity,
  LayoutGrid,
  CalendarDays,
  Settings,
} from "lucide-react";
import { useI18n } from "../context/I18nContext";

const Topbar = () => {
  const { t } = useI18n();
  return (
    <div className="fixed top-4 left-4 right-4 z-50 flex justify-between items-center pointer-events-none">
      <Link
        to="/#portfolio"
        data-testid="demo-back"
        className="pointer-events-auto inline-flex items-center gap-2 bg-zinc-950/80 backdrop-blur border border-white/10 px-3 py-2 text-xs text-zinc-200 hover:bg-zinc-900"
      >
        <ArrowLeft size={12} /> {t("demos.back")}
      </Link>
      <Link
        to="/simulateur"
        data-testid="demo-cta-quote"
        className="pointer-events-auto inline-flex items-center gap-2 bg-white text-zinc-950 px-3 py-2 text-xs hover:bg-zinc-200"
      >
        {t("demos.cta_quote")} <ArrowUpRight size={12} />
      </Link>
    </div>
  );
};

const initialTasks = [
  { id: 1, title: "Validation des wireframes — Acme Corp", project: "Acme · Site web", done: true },
  { id: 2, title: "Brief client — Visuels homepage", project: "Acme · Site web", done: true },
  { id: 3, title: "Maquette mobile — Page produit", project: "Lumina Shop", done: false },
  { id: 4, title: "Réunion équipe design — Mardi 14h", project: "Interne", done: false },
  { id: 5, title: "Recettage v2.4 — paiement Stripe", project: "Lumina Shop", done: false },
];

export default function ExempleApplication() {
  const [tasks, setTasks] = useState(initialTasks);
  const [tab, setTab] = useState("dashboard");
  const toggle = (id) => setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="demo-application">
      <Topbar />

      <div className="flex min-h-screen pt-14">
        {/* Sidebar */}
        <aside className="w-60 bg-zinc-950 border-r border-white/10 hidden md:flex flex-col">
          <div className="p-6 border-b border-white/10">
            <p className="font-display text-xl tracking-tighter italic">Pléiade</p>
            <p className="text-xs text-zinc-500 mt-1">Workspace · Acme</p>
          </div>
          <nav className="p-3 space-y-0.5">
            {[
              { id: "dashboard", label: "Tableau de bord", icon: LayoutGrid },
              { id: "tasks", label: "Tâches", icon: CheckCircle2 },
              { id: "projects", label: "Projets", icon: FolderKanban },
              { id: "team", label: "Équipe", icon: Users },
              { id: "calendar", label: "Calendrier", icon: CalendarDays },
            ].map((it) => {
              const Icon = it.icon;
              const active = tab === it.id;
              return (
                <button
                  key={it.id}
                  data-testid={`app-tab-${it.id}`}
                  onClick={() => setTab(it.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    active ? "bg-white text-zinc-950" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                  }`}
                >
                  <Icon size={14} strokeWidth={1.5} />
                  {it.label}
                </button>
              );
            })}
          </nav>
          <div className="mt-auto p-4 border-t border-white/10">
            <button className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-white">
              <Settings size={12} /> Paramètres
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 flex flex-col">
          {/* Topbar */}
          <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between bg-zinc-950">
            <div className="flex items-center gap-3 flex-1 max-w-md">
              <Search size={14} className="text-zinc-500" />
              <input
                placeholder="Rechercher tâches, projets, contacts…"
                className="bg-transparent text-sm flex-1 focus:outline-none placeholder:text-zinc-500"
              />
            </div>
            <div className="flex items-center gap-3">
              <button className="relative p-2 hover:bg-zinc-900">
                <Bell size={14} />
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-emerald-400" />
              </button>
              <div className="w-8 h-8 bg-white text-zinc-950 flex items-center justify-center font-display text-xs">
                AC
              </div>
            </div>
          </header>

          <div className="p-6 space-y-8">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">— Bonjour Anaïs</p>
              <h1 className="font-display text-3xl md:text-4xl tracking-tighter">
                Vous avez <em className="italic text-emerald-400">3 tâches</em> à finaliser aujourd'hui.
              </h1>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
              {[
                { label: "Tâches actives", value: "12", trend: "+3", icon: CheckCircle2 },
                { label: "Projets en cours", value: "5", trend: "+1", icon: FolderKanban },
                { label: "Membres équipe", value: "8", trend: "—", icon: Users },
                { label: "Productivité", value: "84%", trend: "+12%", icon: TrendingUp },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-zinc-950 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <Icon size={14} strokeWidth={1.4} className="text-zinc-400" />
                      <span className="font-mono text-[10px] text-emerald-400">{s.trend}</span>
                    </div>
                    <p className="font-display text-3xl tracking-tighter">{s.value}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">{s.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Two cols */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 border border-white/10 bg-zinc-950">
                <div className="p-5 border-b border-white/10 flex items-center justify-between">
                  <p className="font-display text-lg">Tâches du jour</p>
                  <button className="text-xs text-zinc-400 inline-flex items-center gap-1 hover:text-white">
                    <Plus size={12} /> Nouvelle tâche
                  </button>
                </div>
                <ul className="divide-y divide-white/10">
                  {tasks.map((t) => (
                    <li key={t.id} className="p-4 flex items-center gap-4 hover:bg-zinc-900/50 transition-colors">
                      <button
                        data-testid={`app-task-${t.id}`}
                        onClick={() => toggle(t.id)}
                        className="text-zinc-500 hover:text-white"
                      >
                        {t.done ? (
                          <CheckCircle2 size={16} className="text-emerald-400" />
                        ) : (
                          <Circle size={16} />
                        )}
                      </button>
                      <div className="flex-1">
                        <p className={`text-sm ${t.done ? "text-zinc-500 line-through" : "text-zinc-100"}`}>
                          {t.title}
                        </p>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-0.5">{t.project}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border border-white/10 bg-zinc-950 p-5">
                <div className="flex items-center justify-between mb-5">
                  <p className="font-display text-lg">Activité</p>
                  <Activity size={14} className="text-zinc-400" />
                </div>
                {/* Mini chart */}
                <svg viewBox="0 0 280 120" className="w-full h-32">
                  <defs>
                    <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor="#10b981" stopOpacity="0.5" />
                      <stop offset="1" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <polyline
                    fill="none"
                    stroke="#fafafa"
                    strokeWidth="1.5"
                    points="0,90 40,60 80,75 120,40 160,55 200,30 240,45 280,20"
                  />
                  <polygon
                    fill="url(#g)"
                    points="0,90 40,60 80,75 120,40 160,55 200,30 240,45 280,20 280,120 0,120"
                  />
                </svg>
                <div className="mt-4 space-y-3 text-xs">
                  {[
                    { who: "Marc P.", what: "a clos la tâche « Validation wireframes »", when: "il y a 12 min" },
                    { who: "Léa R.", what: "a ajouté un commentaire sur Lumina Shop", when: "il y a 1h" },
                    { who: "Vous", what: "avez créé le projet « Refonte 2026 »", when: "il y a 3h" },
                  ].map((a, i) => (
                    <div key={i} className="border-l border-white/10 pl-3">
                      <p className="text-zinc-300">
                        <span className="font-medium">{a.who}</span> {a.what}
                      </p>
                      <p className="text-zinc-500 mt-0.5 font-mono text-[10px]">{a.when}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <p className="text-xs text-zinc-600 text-center pt-6 border-t border-white/10 italic">
              Démo interactive — construite par Versant Studio
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
